import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { logout } from '../../store';
import { useTheme } from '../../context/ThemeContext';
import { useI18n } from '../../context/I18nContext';
import { Menu, Moon, Sun, X } from 'lucide-react';
import LanguageSelect from './LanguageSelect';

const LINK_CLS = 'text-[15px] font-medium text-[#1C1A22] dark:text-stone-200 hover:text-[#9A4357] dark:hover:text-[#E8A5B4] transition';
const ICON_BTN_CLS = 'w-10 h-10 rounded-full flex items-center justify-center text-[#4A4651] dark:text-stone-300 hover:bg-[#F7F4F2] dark:hover:bg-[#2a2a3a] transition';

export default function Navbar() {
  const { user } = useSelector((s) => s.auth);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { dark, setDark } = useTheme();
  const { t } = useI18n();
  const [menuOpen, setMenuOpen] = useState(false);

  const handleLogout = () => { dispatch(logout()); navigate('/'); };

  return (
    <nav className="bg-white/90 dark:bg-[#13131a]/90 backdrop-blur border-b border-[#ECE7E3] dark:border-[#2a2a3a] sticky top-0 z-50 transition-colors">
      <div className="max-w-6xl mx-auto px-4 md:px-6 flex items-center justify-between h-16">
        <Link to="/dashboard" className="font-playfair text-2xl font-semibold text-[#1C1A22] dark:text-stone-100">
          {t('nav.brand')}
        </Link>

        {/* Desktop nav */}
        <div className="hidden md:flex items-center gap-6">
          <Link to="/dashboard" className={LINK_CLS}>{t('nav.dashboard')}</Link>
          <Link to="/pricing" className={LINK_CLS}>{t('nav.pricing')}</Link>
          <LanguageSelect />

          {/* Dark mode toggle */}
          <button
            type="button"
            onClick={() => setDark(!dark)}
            aria-label={dark ? t('nav.ariaLightMode') : t('nav.ariaDarkMode')}
            className={ICON_BTN_CLS}
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>

          <div className="relative">
            <button
              type="button"
              onClick={() => setMenuOpen(!menuOpen)}
              aria-expanded={menuOpen}
              aria-haspopup="true"
              className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full border border-[#ECE7E3] dark:border-[#2a2a3a] text-[#1C1A22] dark:text-stone-200 hover:border-[#9A4357] dark:hover:border-[#E8A5B4] transition"
            >
              <div className="w-8 h-8 rounded-full bg-[#9A4357] flex items-center justify-center font-semibold text-white text-sm">
                {user?.firstName?.[0]?.toUpperCase()}
              </div>
              <span className="text-[15px] font-medium">{user?.firstName}</span>
            </button>
            {menuOpen && (
              <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-[#1e1e2a] rounded-2xl shadow-[0_18px_40px_rgba(28,26,34,0.10)] dark:shadow-black/40 border border-[#ECE7E3] dark:border-[#2a2a3a] py-1">
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30 transition"
                >
                  {t('nav.signOut')}
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Mobile right side */}
        <div className="md:hidden flex items-center gap-2">
          <LanguageSelect className="hidden sm:inline-flex" />
          <button
            type="button"
            onClick={() => setDark(!dark)}
            aria-label={dark ? t('nav.ariaLightMode') : t('nav.ariaDarkMode')}
            className={ICON_BTN_CLS}
          >
            {dark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
          <button
            type="button"
            aria-label={t('nav.openMenu') || 'Open menu'}
            aria-expanded={menuOpen}
            className="w-11 h-11 flex items-center justify-center text-[#1C1A22] dark:text-stone-200"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {menuOpen && (
        <div className="md:hidden bg-white dark:bg-[#13131a] border-t border-[#ECE7E3] dark:border-[#2a2a3a] px-4 py-3 space-y-1">
          <Link to="/dashboard" onClick={() => setMenuOpen(false)} className={`block py-3 ${LINK_CLS}`}>{t('nav.dashboard')}</Link>
          <Link to="/pricing" onClick={() => setMenuOpen(false)} className={`block py-3 ${LINK_CLS}`}>{t('nav.pricing')}</Link>
          <div className="py-2"><LanguageSelect /></div>
          <button type="button" onClick={handleLogout} className="block py-2 text-red-600 dark:text-red-400 w-full text-left">{t('nav.signOut')}</button>
        </div>
      )}
    </nav>
  );
}
